import { Maybe } from '@passionware/monads';
import { z } from 'zod';

export type EnumFilter<V> = {
  operator: 'matchAny' | 'matchAll' | 'matchNone' | 'empty' | 'notEmpty';
  value: V[];
};

export type EnumFilterSelectableOperator =
  | 'matchAny'
  | 'matchAll'
  | 'matchNone';

function isSelectableOperator(
  operator: EnumFilter<unknown>['operator']
): operator is EnumFilterSelectableOperator {
  return (
    operator === 'matchAny' ||
    operator === 'matchAll' ||
    operator === 'matchNone'
  );
}

const ensureSelectableOperator = <V>(filter: EnumFilter<V>): EnumFilter<V> => {
  if (isSelectableOperator(filter.operator)) {
    return filter;
  }
  return {
    operator: 'matchAny',
    value: []
  };
};

export const enumFilter = {
  isSelectableOperator,
  isIncluded<V>(filter: EnumFilter<V>, value: V): boolean {
    return filter.value.includes(value);
  },
  include<V>(filter: EnumFilter<V>, value: V): EnumFilter<V> {
    const normalized = ensureSelectableOperator(filter);
    if (enumFilter.isIncluded(normalized, value)) return normalized;
    return { ...normalized, value: [...normalized.value, value] };
  },
  exclude<V>(filter: EnumFilter<V>, value: V): EnumFilter<V> {
    const normalized = ensureSelectableOperator(filter);
    return {
      ...normalized,
      value: normalized.value.filter(v => v !== value)
    };
  },
  toggle<V>(filter: EnumFilter<V>, value: V): EnumFilter<V> {
    const normalized = ensureSelectableOperator(filter);
    return enumFilter.isIncluded(normalized, value)
      ? enumFilter.exclude(normalized, value)
      : enumFilter.include(normalized, value);
  },
  orDefault<V>(filter: Maybe<EnumFilter<V>>): EnumFilter<V> {
    if (filter) return filter;
    return { operator: 'matchAny', value: [] };
  },
  ofOneOf<V>(value: V[]) {
    return { operator: 'matchAny', value } satisfies EnumFilter<V>;
  },
  ofMatchNone<V>(value: V[]): EnumFilter<V> {
    return { operator: 'matchNone', value };
  },
  matches<V>(
    filter: Maybe<EnumFilter<V>>,
    value: V,
    equalFn: (a: V, b: V) => boolean = (a, b) => a === b
  ): boolean {
    if (!filter) return true;
    switch (filter.operator) {
      case 'matchAny':
        // Empty array means "match all"
        if (filter.value.length === 0) return true;
        return filter.value.some(v => equalFn(v, value));
      case 'matchAll':
        // Exhaustiveness only — use matchesArray for set fields.
        return enumFilter.matchesArray(filter, [value]);
      case 'matchNone':
        // Empty array means "match all" (no exclusions)
        if (filter.value.length === 0) return true;
        return !filter.value.some(v => equalFn(v, value));
      case 'empty':
        return value === null || value === undefined;
      case 'notEmpty':
        return value !== null && value !== undefined;
    }
  },
  matchesArray<V>(
    filter: Maybe<EnumFilter<V>>,
    values: V[],
    equalFn: (a: V, b: V) => boolean = (a, b) => a === b
  ): boolean {
    if (!filter) return true;
    switch (filter.operator) {
      case 'matchAny':
        return values.some(value => filter.value.includes(value));
      case 'matchAll':
        // [].every(...) is true — empty selection matches everything
        return filter.value.every(value => values.includes(value));
      case 'matchNone':
        return values.every(value => !filter.value.includes(value));
      case 'empty':
        return values.length === 0;
      case 'notEmpty':
        return values.length > 0;
    }
  }
};

export const enumFilterSchema = <V>(
  valueSchema: z.ZodType<V>
): z.ZodType<EnumFilter<V>> =>
  z.object({
    operator: z.enum([
      'matchAny',
      'matchAll',
      'matchNone',
      'empty',
      'notEmpty'
    ] as const),
    value: z.array(valueSchema)
  });
